import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Input, Card, Typography, Space, Alert } from 'antd';
import { PhoneOutlined, KeyOutlined, ArrowLeftOutlined, ArrowRightOutlined, EditOutlined } from '@ant-design/icons';
import LanguageSwitcher from './LanguageSwitcher';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { LoginRequest, SendOtpRequest } from '../types';

const { Title, Text } = Typography;

const LoginPage: React.FC = () => {
  const { isRTL } = useLocale();
  const { login } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<'PHONE' | 'CODE'>('PHONE');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 1: Validate phone and proceed to code input
  const handleRequestOtp = async () => {
    if (!phoneNumber || phoneNumber.trim().length < 10) {
      setError('لطفاً شماره موبایل معتبر (مثال: 09120000000) را وارد کنید.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const cleanPhoneNumber = phoneNumber.trim().startsWith('0') 
        ? phoneNumber.trim().substring(1) 
        : phoneNumber.trim();

      const data: SendOtpRequest = {
        phoneNumber: cleanPhoneNumber,
        countryCode: '+98'
      };

      await apiService.sendOtp(data);
      setStep('CODE');
    } catch (err: any) {
      console.error('OTP request error:', err);
      setError(err.response?.data?.message || 'این شماره موبایل در سامانه مجاز نمی‌باشد.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Submit password/code to login
  const handleVerifyAndLogin = async () => {
    if (!code || code.trim().length === 0) {
      setError('لطفاً کد تایید یا رمز عبور را وارد کنید.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const cleanPhoneNumber = phoneNumber.trim().startsWith('0') 
        ? phoneNumber.trim().substring(1) 
        : phoneNumber.trim();

      const data: LoginRequest = {
        phoneNumber: cleanPhoneNumber,
        password: code.trim(),
        otp: code.trim()
      };

      const result = await apiService.login(data);

      login(result.accessToken, {
        phoneNumber: cleanPhoneNumber,
        role: result.user?.role,
        firstName: result.user?.firstName || (result.user as any)?.full_name?.split(' ')[0] || 'کاربر',
        lastName: result.user?.lastName || (result.user as any)?.full_name?.split(' ').slice(1).join(' ') || '',
        id: result.user?.id,
      });

      navigate('/', { replace: true });
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'کد تایید یا رمز عبور نامعتبر است.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #9333ea 100%)',
      padding: '20px',
      direction: 'rtl'
    }}>
      {/* Language Switcher */}
      <div style={{ 
        position: 'absolute',
        top: '20px',
        ...(isRTL ? { left: '20px', right: 'auto' } : { right: '20px', left: 'auto' }),
        zIndex: 1000
      }}>
        <LanguageSwitcher type="button" />
      </div>
      
      {/* Branding Logo & Title */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '28px',
        zIndex: 10
      }}>
        <div style={{
          width: '85px',
          height: '85px',
          borderRadius: '24px',
          background: 'rgba(255,255,255,0.18)',
          backdropFilter: 'blur(12px)',
          border: '2px solid rgba(255,255,255,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 12px 35px rgba(0,0,0,0.25)',
          margin: '0 auto 16px auto',
          transition: 'all 0.3s ease'
        }}>
          <img 
            src="/logo.png" 
            alt="پرتو ۳۶۰" 
            style={{ 
              width: '56px', 
              height: '56px', 
              borderRadius: '12px',
              filter: 'brightness(0) invert(1)',
              objectFit: 'contain'
            }} 
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fallbackDiv = document.createElement('div');
              fallbackDiv.style.cssText = `
                font-size: 28px;
                color: white;
                font-weight: 800;
                font-family: 'Vazirmatn', sans-serif;
              `;
              fallbackDiv.textContent = '۳۶۰';
              e.currentTarget.parentNode?.appendChild(fallbackDiv);
            }}
          />
        </div>
        <Title level={2} style={{ color: 'white', margin: 0, fontWeight: 800, letterSpacing: '-0.5px' }}>
          پرتو ۳۶۰
        </Title>
        <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px' }}>
          سامانه یکپارچه مدیریت سفر و گردشگری
        </Text>
      </div>

      <Card 
        style={{ 
          width: '100%', 
          maxWidth: 400,
          borderRadius: '22px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}
        bodyStyle={{ padding: '28px' }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          
          <div style={{ textAlign: 'center' }}>
            <Title level={4} style={{ margin: 0, fontWeight: 700, color: '#1e293b' }}>
              {step === 'PHONE' ? 'ورود به سامانه' : 'تایید هویت'}
            </Title>
            <Text type="secondary" style={{ fontSize: '13px', marginTop: '4px', display: 'block' }}>
              {step === 'PHONE' 
                ? 'لطفاً شماره موبایل خود را وارد نمایید' 
                : `کد تایید ارسال‌شده به شماره ${phoneNumber} را وارد کنید`}
            </Text>
          </div>

          {step === 'PHONE' ? (
            <Form layout="vertical" onFinish={handleRequestOtp}>
              <Form.Item 
                label={<span style={{ fontWeight: 600 }}>شماره موبایل</span>}
                style={{ marginBottom: '24px' }}
              >
                <Input
                  size="large"
                  autoFocus
                  prefix={<PhoneOutlined style={{ color: '#6366f1', marginLeft: '8px' }} />}
                  value={phoneNumber}
                  onChange={(e) => {
                    setPhoneNumber(e.target.value);
                    if (error) setError('');
                  }}
                  placeholder="مثال: 0912xxxxxxx"
                  style={{ 
                    borderRadius: '12px',
                    height: '52px',
                    direction: 'ltr',
                    textAlign: 'left',
                    fontSize: '16px'
                  }}
                />
              </Form.Item>

              <Button 
                type="primary" 
                htmlType="submit"
                size="large"
                loading={loading}
                icon={<ArrowLeftOutlined />}
                style={{ 
                  width: '100%',
                  height: '52px',
                  borderRadius: '14px',
                  fontSize: '16px',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  border: 'none',
                  boxShadow: '0 8px 20px rgba(79, 70, 229, 0.35)',
                  transition: 'all 0.3s ease'
                }}
              >
                ورود
              </Button>
            </Form>
          ) : (
            <Form layout="vertical" onFinish={handleVerifyAndLogin}>
              <Form.Item 
                label={
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <span style={{ fontWeight: 600 }}>کد تایید</span>
                    <Button 
                      type="link" 
                      size="small" 
                      icon={<EditOutlined />}
                      onClick={() => {
                        setStep('PHONE');
                        setCode('');
                        setError('');
                      }}
                      style={{ padding: 0, fontSize: '12px' }}
                    >
                      ویرایش شماره
                    </Button>
                  </div>
                }
                style={{ marginBottom: '24px' }}
              >
                <Input.Password
                  size="large"
                  autoFocus
                  prefix={<KeyOutlined style={{ color: '#6366f1', marginLeft: '8px' }} />}
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    if (error) setError('');
                  }}
                  placeholder="کد تایید را وارد کنید"
                  style={{ 
                    borderRadius: '12px',
                    height: '52px',
                    direction: 'ltr',
                    textAlign: 'left',
                    fontSize: '16px'
                  }}
                />
              </Form.Item>

              <Button 
                type="primary" 
                htmlType="submit"
                size="large"
                loading={loading}
                icon={<ArrowLeftOutlined />}
                style={{ 
                  width: '100%',
                  height: '52px',
                  borderRadius: '14px',
                  fontSize: '16px',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  border: 'none',
                  boxShadow: '0 8px 20px rgba(79, 70, 229, 0.35)',
                  transition: 'all 0.3s ease'
                }}
              >
                تایید و ورود
              </Button>
            </Form>
          )}

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError('')}
              style={{ borderRadius: '10px' }}
            />
          )}

        </Space>
      </Card>
      
      <div style={{ marginTop: '24px', textAlign: 'center' }}>
        <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>
          © ۱۴۰۵ تمامی حقوق محفوظ است • سامانه پرتو ۳۶۰
        </Text>
      </div>
    </div>
  );
};

export default LoginPage;
